import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CoverFinder } from "@/components/CoverFinder";
import { BulkButton, BulkProgress, useBulkJob } from "@/components/BulkArtJob";
import { NearMissReview } from "@/components/NearMissReview";
import { clearRestoreCache } from "@/lib/restore";

export default function MissingArt() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  const load = async () => {
    clearRestoreCache();
    try { setData((await api.get("/artwork/missing")).data); } catch (e) { toast.error(formatApiError(e)); setData({ albums: [], missing: 0, total_albums: 0 }); }
  };
  useEffect(() => { load(); }, []);
  const job = useBulkJob("/artwork/fix-all", { noun: "covers", onDone: load });
  const stillMissing = new Set((data?.albums || []).map((a) => a.id));
  const nearRows = job.running ? [] : (job.status?.skipped || []).filter((x) => x.near?.length && stillMissing.has(x.id))
    .map((x) => ({ key: x.id, title: x.name, subtitle: x.artist, near: x.near, item: { album_id: x.id } }));

  if (!data) return <p className="flex items-center gap-2 text-sm text-zinc-500" data-testid="missing-art-loading"><Loader2 size={14} className="animate-spin" />Checking your albums…</p>;
  return (
    <div className="animate-rise" data-testid="missing-art-page">
      <PageHeader eyebrow={`${data.missing} of ${data.total_albums} albums`} title="Missing album art" testid="missing-art-title">
        {(data.albums.length > 0 || job.running) && <BulkButton job={job} label="Find covers for all" testid="fix-all-button" />}
        <Link to="/albums" className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="missing-art-back">Back to albums</Link>
      </PageHeader>
      <BulkProgress job={job} noun="covers" testid="fix-all-status" onRetry={job.start} />
      <NearMissReview rows={nearRows} noun="cover" onSaved={load} testid="near-miss-albums" />
      {data.albums.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 px-8 py-16 text-center" data-testid="missing-art-empty">
          <Disc3 size={32} strokeWidth={1.25} className="mx-auto text-emerald-600" />
          <p className="mt-4 font-heading text-lg">Every album has a cover</p>
          <p className="mt-1 text-sm text-zinc-500">Nothing to fix here. New albums without art will show up on this page.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5" data-testid="missing-art-grid">
          {data.albums.map((a) => (
            <li key={a.id} className="group" data-testid={`missing-art-${a.id}`}>
              <button type="button" onClick={() => setActive(a)} className="relative block w-full overflow-hidden rounded-md bg-zinc-100 text-left" data-testid={`missing-art-find-${a.id}`}>
                <div className="flex aspect-square items-center justify-center"><Disc3 size={36} strokeWidth={1.25} className="text-zinc-300" /></div>
                <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-zinc-900/85 py-2 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                  <ImagePlus size={12} />Find cover
                </span>
              </button>
              <Link to={`/album/${a.id}`} className="mt-2 block truncate text-sm font-medium text-zinc-900 hover:underline">{a.name}</Link>
              <p className="truncate text-xs text-zinc-500">{a.artist}{a.year ? ` · ${a.year}` : ""} · {a.song_count} {a.song_count === 1 ? "song" : "songs"}</p>
            </li>
          ))}
        </ul>
      )}
      <CoverFinder album={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} onApplied={load} />
    </div>
  );
}
